import { useState } from 'react';
import { signOut } from 'aws-amplify/auth';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// @mui material components
import Icon from '@mui/material/Icon';
import { Box, Typography } from '@mui/material';

// components for Sidenav
import SidenavCollapse from './SidenavCollapse';
import SidenavList from './SidenavList';
import SidenavItem from './SidenavItem';

// Custom styles for the Sidenav
import SidenavRoot from './SidenavRoot';
import sidenavLogoLabel from './styles/sidenav';

import PropTypes from 'prop-types';

// Images
import PersonIcon from '@mui/icons-material/Person';

import localData from 'utils/localData';
import brandWhite from 'assets/images/logos/iwg/iwg-white-logo-cropped.png';

import { useMaterialUIController, setMiniSidenav } from 'context';
import { HorizontalDivider } from './styles';
import { LOGOUT_URL } from 'shared/constants/apis';
import logger from 'utils/logger';
import { useAuth } from 'views/root/AuthProvider';
import ADMIN_ROUTES from './routes';

const propTypes = {
  color: PropTypes.string,
};

const log = logger.createLogger('AdminSidenav');

const AdminSidenav = ({ color }) => {
  const [openCollapse, setOpenCollapse] = useState(false);

  const { currentUser } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const { pathname } = location;

  // Username is not
  const username = currentUser.username;

  let textColor = 'white';

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const handleSignOut = async () => {
    try {
      // New Amplify v6 signOut with options
      await signOut({
        global: true, // Sign out from all devices
      });

      // Clear local storage
      localData.clear();

      // Redirect to logout URL
      window.location.assign(
        import.meta.env.VITE_LOGOUT_REDIRECT_URL || LOGOUT_URL
      );
    } catch (error) {
      log.error('Error signing out:', error);
    }
  };

  return (
    <SidenavRoot
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}>
      <Box pt={3} pb={1} px={4} textAlign="center">
        <Box
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}>
          <Typography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </Typography>
        </Box>
        <Box component={NavLink} to="/" display="flex" alignItems="center">
          <Box component="img" src={brandWhite} alt="Brand" width="3rem" />
          <Box
            width={'70%'}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <Typography
              component="h1"
              variant="button"
              fontWeight="bold"
              color={textColor}>
              DOCloud Admin
            </Typography>
          </Box>
        </Box>
      </Box>
      <HorizontalDivider />

      <SidenavCollapse
        key="profile"
        name={username}
        icon={<PersonIcon fontSize="large" />}
        active={openCollapse}
        open={openCollapse}
        onClick={() => {
          setOpenCollapse((prevState) => !prevState);
        }}>
        <SidenavList key={'logout'}>
          <SidenavItem color={color} name="LOGOUT" onClick={handleSignOut} />
        </SidenavList>
      </SidenavCollapse>
      <HorizontalDivider />

      {ADMIN_ROUTES.map((route) => (
        <NavLink to={route.path} key={route.path}>
          <SidenavCollapse
            name={route.name}
            noCollapse={true}
            icon={route.icon}
            active={pathname === route.path}
          />
        </NavLink>
      ))}
    </SidenavRoot>
  );
};

AdminSidenav.propTypes = propTypes;

export default AdminSidenav;
